import { type FC, useCallback, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { _isNotBlank, _notNil } from '@/littledash';
import Button from '@/components/UI/Button/Button';
import type { FieldConfig } from '../VerticalStepForm.model';
import SelectField from '../Fields/SelectField';
import UploadField from '../Fields/UploadField';
import styles from './StepContent.module.scss';

interface StepContentProps {
  stepId: string;
  fields: Array<FieldConfig>;
  description?: string;
  required: boolean;
  advanceLabel?: string;
  isAdvancing?: boolean;
  onAdvance: (stepId: string) => void;
  onSkip: (stepId: string) => void;
  onValueChange: (stepId: string) => void;
}

/** Expanded step body. Resolves and renders fields from config, with Next and Skip action buttons. */
const StepContent: FC<StepContentProps> = ({
  stepId,
  fields,
  description,
  required,
  advanceLabel,
  isAdvancing = false,
  onAdvance,
  onSkip,
  onValueChange,
}) => {
  const { control } = useFormContext();
  const fieldNames = useMemo(() => fields.map((f) => f.name), [fields]);
  const watchedValues = useWatch({ control, name: fieldNames });
  const hasData = watchedValues.some((value) => {
    // Multi-file upload stores an array of urls — "has data" means at least one entry.
    if (Array.isArray(value)) {
      return value.length > 0;
    }
    // Select stores the full { label, value } option; a primitive field stores the value itself.
    if (_notNil(value) && typeof value === 'object') {
      return _isNotBlank(String(value.value ?? ''));
    }
    return _isNotBlank(String(value ?? ''));
  });

  const handleAdvance = useCallback(() => onAdvance(stepId), [onAdvance, stepId]);
  const handleSkip = useCallback(() => onSkip(stepId), [onSkip, stepId]);
  const handleValueChange = useCallback(() => onValueChange(stepId), [onValueChange, stepId]);

  const renderField = (fieldConfig: FieldConfig) => {
    // Dispatch on the discriminant so each field type receives its own config
    // variant — no casts, and a mis-wired field type fails to compile.
    switch (fieldConfig.type) {
      case 'select':
        return <SelectField key={fieldConfig.name} config={fieldConfig} onValueChange={handleValueChange} />;
      case 'upload':
        return <UploadField key={fieldConfig.name} config={fieldConfig} onValueChange={handleValueChange} />;
      default: {
        // Exhaustiveness guard: adding a FieldConfig variant without a case here
        // becomes a compile error rather than a silently-unrendered field.
        const _exhaustive: never = fieldConfig;
        return _exhaustive;
      }
    }
  };

  return (
    <div
      className={styles.content}
      data-test-component="StepContent"
      data-test-element="container"
      data-testid="step-content"
    >
      {description && <p className={styles.description}>{description}</p>}
      <div className={styles.fields}>{fields.map(renderField)}</div>
      <div className={styles.actions}>
        <Button
          primary
          disabled={!hasData || isAdvancing}
          loading={isAdvancing}
          onClick={handleAdvance}
          testId="step-content__next"
        >
          {advanceLabel ?? 'Next'}
        </Button>
        {!required && (
          <Button plain onClick={handleSkip} testId="step-content__skip">
            Skip
          </Button>
        )}
      </div>
    </div>
  );
};

export default StepContent;
